import { type RouteRecordRaw } from "vue-router";
import MainLayout from "@/layouts/MainLayout.vue";
import { tokenRoutes } from "./routes/token.routes";
import { landingRoutes } from "./routes/landing.routes";
import { getModules } from "core-fe-lib/components-shadcn/shell/registry";
import { buildModuleRoutes } from "core-fe-lib/components-shadcn/shell/router-factory";
import { TENANT_CONFIG_SCHEMA } from "@/config/tenantConfigSchema";
import { GLOBAL_CONFIG_SCHEMA } from "@/config/globalConfigSchema";
import { pipelineLLMFetcher } from "@/composables/llmFetcher";
import enUSCore from "@/i18n/en-US/core";

// Hub's feature inventory — the keys of `core.features` minus the page title.
// Drives the checkboxes on the super-admin tenant features page so newly added
// features (e.g. aiemployee) are always enablable, even before any tenant
// has the flag stored.
const TENANT_FEATURE_KEYS = Object.keys(enUSCore.features).filter(
  (k) => k !== "title"
);

const routes: RouteRecordRaw[] = [
  // Feature module routes (self-registered via shell)
  ...buildModuleRoutes(getModules(), { MainLayout }),

  // Shell-level / core routes (not feature modules)
  tokenRoutes(MainLayout),

  {
    path: "/",
    component: MainLayout,
    children: [
      {
        path: "",
        component: () =>
          import("core-fe-lib/components-shadcn/layouts/PassthroughLayout.vue"),
        children: [
          ...landingRoutes,
          {
            path: "/global-configs",
            name: "global-configs",
            component: () =>
              import("core-fe-lib/components-shadcn/super-admin/GlobalConfigListPage.vue"),
            props: () => ({ schema: GLOBAL_CONFIG_SCHEMA }),
            meta: { requiresAuth: true },
          },
          // Dev-only routes are appended below via the import.meta.env.DEV gate.
          {
            path: "/global-configs/new",
            name: "new-global-config",
            component: () =>
              import("core-fe-lib/components-shadcn/super-admin/GlobalConfigEditPage.vue"),
            props: () => ({ schema: GLOBAL_CONFIG_SCHEMA }),
            meta: { requiresAuth: true },
          },
          {
            path: "/global-configs/:id/edit",
            name: "edit-global-config",
            component: () =>
              import("core-fe-lib/components-shadcn/super-admin/GlobalConfigEditPage.vue"),
            props: () => ({ schema: GLOBAL_CONFIG_SCHEMA }),
            meta: { requiresAuth: true },
          },
          {
            path: "/tenant-configs",
            name: "tenant-configs",
            component: () =>
              import("core-fe-lib/components-shadcn/admin/TenantConfigListPage.vue"),
            props: () => ({ schema: TENANT_CONFIG_SCHEMA }),
            meta: { requiresAuth: true },
          },
          {
            path: "/tenant-configs/new",
            name: "new-tenant-config",
            component: () =>
              import("core-fe-lib/components-shadcn/admin/TenantConfigEditPage.vue"),
            props: () => ({
              schema: TENANT_CONFIG_SCHEMA,
              llmFetcher: pipelineLLMFetcher,
            }),
            meta: { requiresAuth: true },
          },
          {
            path: "/tenant-configs/:id/edit",
            name: "edit-tenant-config",
            component: () =>
              import("core-fe-lib/components-shadcn/admin/TenantConfigEditPage.vue"),
            props: () => ({
              schema: TENANT_CONFIG_SCHEMA,
              llmFetcher: pipelineLLMFetcher,
            }),
            meta: { requiresAuth: true },
          },
          {
            path: "/signin",
            name: "signin",
            component: () =>
              import("core-fe-lib/components-shadcn/auth/SigninPage.vue"),
          },
          {
            path: "/signup",
            name: "signup",
            component: () =>
              import("core-fe-lib/components-shadcn/auth/SignupPage.vue"),
          },
          {
            path: "/no-access",
            name: "no-access",
            component: () =>
              import("core-fe-lib/components-shadcn/auth/NoAccessPage.vue"),
            meta: { requiresAuth: true },
          },
          {
            path: "/recovery",
            name: "recovery",
            component: () =>
              import("core-fe-lib/components-shadcn/auth/RecoveryPage.vue"),
            meta: { requiresAuth: false },
          },
          {
            path: "/user/me/profile",
            name: "me-profile",
            component: () =>
              import("core-fe-lib/components-shadcn/me/MeProfilePage.vue"),
            meta: { requiresAuth: true },
          },
          {
            path: "/user/me/security",
            name: "security-settings",
            component: () =>
              import("core-fe-lib/components-shadcn/me/SecuritySettingsPage.vue"),
            meta: { requiresAuth: true },
          },
          {
            path: "/user/me/password-reset-request",
            name: "password-reset-request",
            component: () =>
              import("core-fe-lib/components-shadcn/auth/PasswordResetRequestPage.vue"),
            meta: { requiresAuth: false },
          },
          {
            path: "/user/me/password-reset-action",
            name: "password-reset-action",
            component: () =>
              import("core-fe-lib/components-shadcn/auth/PasswordResetActionPage.vue"),
            meta: { requiresAuth: true },
          },
          {
            path: "/super-admin/tenants",
            name: "super-admin-tenants",
            component: () =>
              import("core-fe-lib/components-shadcn/super-admin/TenantListPage.vue"),
            meta: { requiresAuth: true },
          },
          {
            path: "/super-admin/tenants/:tenantid/features",
            name: "super-admin-tenant-features",
            component: () =>
              import("core-fe-lib/components-shadcn/super-admin/TenantFeaturesPage.vue"),
            props: () => ({ featureKeys: TENANT_FEATURE_KEYS }),
            meta: { requiresAuth: true },
          },
          {
            path: "/admin/tenant-profile",
            name: "tenant-profile",
            component: () =>
              import("core-fe-lib/components-shadcn/admin/TenantProfilePage.vue"),
            meta: { requiresAuth: true },
          },
          {
            path: "/super-admin/tenants/new",
            name: "super-admin-new-tenant",
            component: () =>
              import("core-fe-lib/components-shadcn/super-admin/TenantEditPage.vue"),
            meta: { requiresAuth: true },
          },
          {
            path: "/super-admin/tenants/:id/edit",
            name: "super-admin-edit-tenant",
            component: () =>
              import("core-fe-lib/components-shadcn/super-admin/TenantEditPage.vue"),
            meta: { requiresAuth: true },
          },
          {
            path: "/super-admin/tenants/:tenantid/users",
            name: "super-admin-users",
            component: () =>
              import("core-fe-lib/components-shadcn/super-admin/UserListPage.vue"),
            meta: { requiresAuth: true },
          },
          {
            path: "/super-admin/tenants/:tenantid/users/new",
            name: "super-admin-new-user",
            component: () =>
              import("core-fe-lib/components-shadcn/super-admin/UserEditPage.vue"),
            meta: { requiresAuth: true },
          },
          {
            path: "/super-admin/tenants/:tenantid/users/:id/edit",
            name: "super-admin-edit-user",
            component: () =>
              import("core-fe-lib/components-shadcn/super-admin/UserEditPage.vue"),
            meta: { requiresAuth: true },
          },
          {
            path: "/users",
            name: "users",
            component: () =>
              import("core-fe-lib/components-shadcn/admin/UserListPage.vue"),
            meta: { requiresAuth: true },
          },
          {
            path: "/users/new",
            name: "new-user",
            component: () =>
              import("core-fe-lib/components-shadcn/admin/UserEditPage.vue"),
            meta: { requiresAuth: true },
          },
          {
            path: "/users/:id/edit",
            name: "edit-user",
            component: () =>
              import("core-fe-lib/components-shadcn/admin/UserEditPage.vue"),
            meta: { requiresAuth: true },
          },
          {
            path: "/verify-email",
            name: "verify-email",
            component: () =>
              import("core-fe-lib/components-shadcn/auth/EmailVerification.vue"),
            meta: { requiresAuth: false },
          },
          {
            path: "/verify/webauthn",
            name: "verify-webauthn",
            component: () =>
              import("core-fe-lib/components-shadcn/auth/VerifyWebauthnPage.vue"),
            meta: { requiresAuth: true },
          },
          {
            path: "/register/webauthn",
            name: "register-webauthn",
            component: () =>
              import("core-fe-lib/components-shadcn/auth/RegisterWebauthnPage.vue"),
            meta: { requiresAuth: true },
          },
          // Dev-only routes (only added in dev builds — tree-shaken in prod)
          ...(import.meta.env.DEV
            ? [
                {
                  path: "/diagnostic",
                  name: "diagnostic",
                  component: () => import("@/dev/DiagnosticPage.vue"),
                  meta: { requiresAuth: false },
                },
                {
                  path: "/components-demo",
                  name: "components-demo",
                  component: () => import("@/dev/ComponentsDemoPage.vue"),
                  meta: { requiresAuth: false },
                },
              ]
            : []),
          // Always leave this as last one
          {
            path: "/:catchAll(.*)*",
            name: "ErrorNotFound",
            component: () =>
              import("core-fe-lib/components-shadcn/shell/NotFoundPage.vue"),
          },
        ],
      },
    ],
  },
];

export default routes;
